import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import MovieGrid from "@/components/feature/MovieGrid";
import SectionHeading from "@/components/feature/SectionHeading";
import Modal from "@/components/base/Modal";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";
import { getMovieById, getRelatedMovies, formatViews } from "@/lib/movies";

interface Comment {
  id: number;
  name: string;
  content: string;
  time: string;
}

const seedComments: Comment[] = [
  { id: 1, name: "追剧小能手", content: "画面质感太顶了，一口气看完根本停不下来，强烈推荐！", time: "2 天前" },
  { id: 2, name: "深夜观影人", content: "节奏把握得很好，看到最后真的被戳中了，值得二刷。", time: "5 天前" },
  { id: 3, name: "爱看剧的猫", content: "配乐和镜头语言都很用心，是近期少见的诚意之作。", time: "1 周前" },
];

export default function Detail() {
  const { id } = useParams();
  const movie = getMovieById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [playOpen, setPlayOpen] = useState(false);
  const [comments, setComments] = useState<Comment[]>(seedComments);
  const [name, setName] = useState("");
  const [content, setContent] = useState("");

  useSeo({
    title: movie ? `${movie.title} - 91免费看片` : "内容不存在 - 91免费看片",
    description: movie
      ? `${movie.title}（${movie.year} · ${movie.region} · ${movie.type}）：${movie.overview.slice(0, 70)}…`
      : "您访问的影视内容不存在，返回首页浏览更多高清影视资源。",
    keywords: movie ? `${movie.title},${movie.genres.join(",")},91免费看片` : undefined,
  });

  if (!movie) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-file-unknow-line text-3xl" />
        </span>
        <h2 className="mt-4 font-heading text-xl font-bold text-foreground-950">
          内容不存在
        </h2>
        <p className="mt-2 text-sm text-foreground-500">
          您访问的影视内容不存在或已下架，去看看其他精彩内容吧。
        </p>
        <Link
          to="/"
          className="mt-6 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelatedMovies(movie, 6);
  const favorited = isFavorite(movie.id);

  const addComment = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !content.trim()) return;
    setComments((prev) => [
      { id: Date.now(), name: name.trim(), content: content.trim(), time: "刚刚" },
      ...prev,
    ]);
    setName("");
    setContent("");
  };

  return (
    <div>
      {/* Hero */}
      <div className="relative w-full overflow-hidden bg-primary-950">
        <img
          src={movie.backdrop}
          alt={`${movie.title} 剧照`}
          title={`${movie.title} 91免费看片`}
          className="absolute inset-0 h-full w-full object-cover object-top opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/40" />
        <div className="relative mx-auto flex w-full max-w-[1280px] flex-col gap-6 px-4 py-8 md:flex-row md:items-end md:px-6 md:py-12">
          <div className="w-40 shrink-0 overflow-hidden rounded-lg md:w-52">
            <img
              src={movie.poster}
              alt={`${movie.title} 海报`}
              title={`${movie.title} 91免费看片`}
              className="aspect-[2/3] w-full object-cover object-top"
            />
          </div>
          <div className="min-w-0 flex-1">
            <nav className="flex flex-wrap items-center gap-1 text-xs text-background-100/70">
              <Link to="/" className="hover:text-accent-400">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <Link to={`/category/${movie.type}`} className="hover:text-accent-400">
                {movie.type}
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-background-50">{movie.title}</span>
            </nav>

            <h2 className="mt-3 font-heading text-2xl font-extrabold text-white md:text-4xl">
              {movie.title}
            </h2>
            {movie.originalTitle && (
              <p className="mt-1 text-sm text-background-100/60">{movie.originalTitle}</p>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-background-100/80">
              <span className="font-bold text-accent-400">{movie.rating.toFixed(1)} 分</span>
              <span>{movie.year}</span>
              <span>{movie.region}</span>
              <span>{movie.duration}</span>
              <span className="flex items-center gap-1">
                <i className="ri-fire-line text-accent-400" />
                {formatViews(movie.views)}次播放
              </span>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              {movie.genres.map((g) => (
                <Link
                  key={g}
                  to={`/category/${movie.type}`}
                  className="rounded-full border border-white/25 px-3 py-1 text-xs text-background-50 transition-colors hover:border-accent-400 hover:text-accent-400"
                >
                  {g}
                </Link>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setPlayOpen(true)}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-play-fill text-lg" /> 立即播放
              </button>
              <button
                type="button"
                onClick={() => toggleFavorite(movie.id)}
                className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border px-5 py-2.5 text-sm font-semibold transition-colors ${
                  favorited
                    ? "border-accent-400 bg-accent-500/15 text-accent-400"
                    : "border-white/40 text-white hover:bg-white/10"
                }`}
              >
                <i className={favorited ? "ri-heart-fill text-lg" : "ri-heart-line text-lg"} />
                {favorited ? "已收藏" : "收藏"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
          <div>
            {/* Intro */}
            <section className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h3 className="font-heading text-lg font-bold text-foreground-950">
                剧情简介
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                {movie.overview}
              </p>
            </section>

            {/* Comments */}
            <section className="mt-8">
              <SectionHeading
                title="用户评论"
                subtitle={`共 ${comments.length} 条评论 · 演示功能，不实际存储`}
                icon="ri-chat-3-line"
              />
              <form
                onSubmit={addComment}
                className="rounded-xl border border-background-200 bg-background-50 p-4"
              >
                <div className="flex flex-col gap-3 sm:flex-row">
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="你的昵称"
                    className="w-full rounded-md border border-background-300 bg-background-100 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none sm:w-40"
                  />
                  <input
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="写下你的看法…"
                    className="w-full flex-1 rounded-md border border-background-300 bg-background-100 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                  <button
                    type="submit"
                    className="whitespace-nowrap rounded-md bg-primary-600 px-5 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
                  >
                    发表
                  </button>
                </div>
                <p className="mt-2 text-xs text-foreground-400">
                  温馨提示：评论仅为演示交互，刷新页面后不会保留。
                </p>
              </form>

              <ul className="mt-4 space-y-3">
                {comments.map((c) => (
                  <li
                    key={c.id}
                    className="flex gap-3 rounded-xl border border-background-200 bg-background-50 p-4"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-100 font-semibold text-secondary-700">
                      {c.name.slice(0, 1)}
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground-900">
                          {c.name}
                        </span>
                        <span className="text-xs text-foreground-400">{c.time}</span>
                      </div>
                      <p className="mt-1 text-sm leading-relaxed text-foreground-700">
                        {c.content}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          {/* Side info */}
          <aside className="space-y-4">
            <div className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">
                影片信息
              </h3>
              <dl className="mt-3 space-y-3 text-sm">
                {[
                  { label: "导演", value: movie.director },
                  { label: "主演", value: movie.actors.join(" / ") },
                  { label: "地区", value: movie.region },
                  { label: "年份", value: `${movie.year}` },
                  { label: "片长", value: movie.duration },
                  { label: "类型", value: movie.type },
                ].map((row) => (
                  <div key={row.label} className="flex gap-3">
                    <dt className="w-12 shrink-0 text-foreground-400">{row.label}</dt>
                    <dd className="text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-4 flex flex-wrap gap-2 border-t border-background-200 pt-4">
                {movie.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-700"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-xl bg-primary-600 p-5 text-background-50">
              <h3 className="font-heading text-base font-bold">播放说明</h3>
              <p className="mt-2 text-xs leading-relaxed text-background-100/85">
                本站为演示站点，暂未接入真实流媒体接口。点击「立即播放」将展示播放交互演示。
              </p>
            </div>
          </aside>
        </div>

        {/* Related */}
        <section className="mt-12">
          <SectionHeading
            title="相关推荐"
            subtitle="根据题材与标签为你精选"
            icon="ri-magic-line"
            moreTo={`/category/${movie.type}`}
          />
          <MovieGrid movies={related} />
        </section>
      </div>

      <Modal
        open={playOpen}
        title="播放功能演示"
        icon="ri-play-circle-line"
        onClose={() => setPlayOpen(false)}
      >
        <p className="text-sm leading-relaxed text-foreground-700">
          当前为演示站点，暂未接入真实视频播放接口，因此无法直接观看《{movie.title}》。
        </p>
        <p className="mt-2 text-sm leading-relaxed text-foreground-500">
          正式版本将支持高清在线播放、多线路切换与播放进度记忆。感谢你的理解与支持。
        </p>
        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setPlayOpen(false)}
            className="whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
          >
            我知道了
          </button>
          <Link
            to="/special"
            onClick={() => setPlayOpen(false)}
            className="whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
          >
            浏览影视专题
          </Link>
        </div>
      </Modal>
    </div>
  );
}